import { api } from './api'

export const PORTONE_V2_PROVIDER = 'portone'
const PENDING_PAYMENT_STORAGE_KEY = 'billing.portone.v2.pending-payment'
const PENDING_BILLING_KEY_STORAGE_KEY = 'billing.portone.v2.pending-billing-key'

function responseData(response) {
  return response?.data?.data ?? response?.data ?? null
}

function required(value, message) {
  if (value == null || String(value).trim() === '') {
    throw new Error(message)
  }
  return String(value).trim()
}

function pendingStorage() {
  if (typeof window === 'undefined') return null
  return window.sessionStorage
}

function savePendingPayment(order) {
  const storage = pendingStorage()
  if (!storage) return
  storage.setItem(PENDING_PAYMENT_STORAGE_KEY, JSON.stringify({
    orderId: order.id ?? null,
    orderNo: order.orderNo,
    amount: order.amount,
    currency: order.currency,
    gatewayChannelCode: order.gatewayChannelCode || null,
  }))
}

function readPendingPayment() {
  const storage = pendingStorage()
  if (!storage) return null
  try {
    return JSON.parse(storage.getItem(PENDING_PAYMENT_STORAGE_KEY) || 'null')
  } catch {
    return null
  }
}

function clearPendingPayment() {
  pendingStorage()?.removeItem(PENDING_PAYMENT_STORAGE_KEY)
}

function savePendingBillingKey(prepared) {
  const storage = pendingStorage()
  if (!storage) return
  storage.setItem(PENDING_BILLING_KEY_STORAGE_KEY, JSON.stringify({
    planCode: prepared.planCode,
    channelCode: prepared.channelCode,
    issueId: prepared.issueId,
  }))
}

function readPendingBillingKey() {
  const storage = pendingStorage()
  if (!storage) return null
  try {
    return JSON.parse(storage.getItem(PENDING_BILLING_KEY_STORAGE_KEY) || 'null')
  } catch {
    return null
  }
}

function clearPendingBillingKey() {
  pendingStorage()?.removeItem(PENDING_BILLING_KEY_STORAGE_KEY)
}

function searchParams(search) {
  return new URLSearchParams(
    search ?? (typeof window !== 'undefined' ? window.location.search : ''),
  )
}

async function loadPortOneV2() {
  return import('@portone/browser-sdk/v2')
}

export async function getPortOneV2CheckoutConfig() {
  const config = responseData(await api.get('/billing/payment-gateways/portone/config'))
  const channels = Array.isArray(config?.channels) ? config.channels : []
  return {
    provider: config?.provider || PORTONE_V2_PROVIDER,
    version: config?.version || 'V2',
    enabled: Boolean(config?.enabled),
    ready: Boolean(config?.ready),
    storeId: config?.storeId || null,
    channelKey: config?.channelKey || null,
    defaultChannelCode: config?.defaultChannelCode || null,
    channels,
    recurringEnabled: Boolean(config?.recurringEnabled),
  }
}

export async function requestPortOneV2RecurringSubscription({
  planCode,
  channelCode = null,
  redirectUrl = typeof window !== 'undefined' ? window.location.href : null,
} = {}) {
  const config = await getPortOneV2CheckoutConfig()
  if (!config.recurringEnabled) {
    throw new Error('\uD604\uC7AC \uC6F9 \uC790\uB3D9 \uAC31\uC2E0 \uACB0\uC81C\uB97C \uC900\uBE44 \uC911\uC785\uB2C8\uB2E4.')
  }

  const prepared = responseData(await api.post(
    '/billing/payment-gateways/portone/recurring/prepare',
    {
      planCode: required(planCode, '\uAD6C\uB3C5 \uD50C\uB79C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
      channelCode: channelCode || config.defaultChannelCode,
    },
  ))
  savePendingBillingKey(prepared)

  const PortOne = await loadPortOneV2()
  const issueRequest = {
    storeId: required(prepared?.storeId, '\uD3EC\uD2B8\uC6D0 \uC0C1\uC810 \uC124\uC815\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    channelKey: required(prepared?.channelKey, '\uD3EC\uD2B8\uC6D0 \uCC44\uB110 \uC124\uC815\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    billingKeyMethod: 'CARD',
    issueId: required(prepared?.issueId, '\uCE74\uB4DC \uB4F1\uB85D \uC2DD\uBCC4\uAC12\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    issueName: required(prepared?.issueName, '\uCE74\uB4DC \uB4F1\uB85D \uC774\uB984\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    customer: prepared?.customer,
    redirectUrl,
  }
  const issued = await PortOne.requestIssueBillingKey(issueRequest)
  if (!issued) {
    return { redirected: true, prepared }
  }
  if (issued.code) {
    clearPendingBillingKey()
    throw new Error(issued.message || '\uCE74\uB4DC\uB97C \uB4F1\uB85D\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  }

  const agreement = responseData(await api.post(
    '/billing/payment-gateways/portone/recurring/activate',
    {
      planCode: prepared.planCode,
      channelCode: prepared.channelCode,
      billingKey: required(issued.billingKey, '\uC815\uAE30\uACB0\uC81C \uC778\uC99D\uC815\uBCF4\uB97C \uD655\uC778\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.'),
      issueId: prepared.issueId,
    },
  ))
  clearPendingBillingKey()
  return { redirected: false, prepared, agreement }
}

export function isPortOneV2RecurringRedirect(search) {
  const params = searchParams(search)
  return params.has('billingKey') || Boolean(readPendingBillingKey())
}

export async function confirmPortOneV2RecurringRedirect(search) {
  const params = searchParams(search)
  const pending = readPendingBillingKey()
  const errorCode = params.get('code')
  if (errorCode) {
    clearPendingBillingKey()
    throw new Error(params.get('message') || '\uCE74\uB4DC\uB97C \uB4F1\uB85D\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  }
  if (!pending) {
    throw new Error('\uB300\uAE30 \uC911\uC778 \uC815\uAE30\uACB0\uC81C \uC694\uCCAD\uC744 \uD655\uC778\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.')
  }
  const issueId = params.get('issueId') || pending.issueId
  if (issueId !== pending.issueId) {
    throw new Error('\uCE74\uB4DC \uB4F1\uB85D \uC815\uBCF4\uAC00 \uB300\uAE30 \uC911\uC778 \uC694\uCCAD\uACFC \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.')
  }
  const agreement = responseData(await api.post(
    '/billing/payment-gateways/portone/recurring/activate',
    {
      planCode: pending.planCode,
      channelCode: pending.channelCode,
      billingKey: required(params.get('billingKey'), '\uC815\uAE30\uACB0\uC81C \uC778\uC99D\uC815\uBCF4\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.'),
      issueId: pending.issueId,
    },
  ))
  clearPendingBillingKey()
  return { redirected: false, agreement }
}

export async function getMyPortOneRecurringAgreement() {
  return responseData(await api.get('/billing/payment-gateways/portone/recurring/me', { __silent: true }))
}

export async function cancelMyPortOneRecurringAgreement() {
  return responseData(await api.post('/billing/payment-gateways/portone/recurring/cancel'))
}

export async function requestPortOneV2SubscriptionPayment({
  planCode,
  period = 'MONTH',
  paymentMethod = 'CARD',
  couponCode = null,
  redirectUrl = typeof window !== 'undefined' ? window.location.href : null,
  customer = null,
  idempotencyKey = null,
  channelCode = null,
} = {}) {
  const config = await getPortOneV2CheckoutConfig()
  if (!config.ready) {
    throw new Error('\uD604\uC7AC \uC6F9 \uACB0\uC81C\uB97C \uC900\uBE44 \uC911\uC785\uB2C8\uB2E4.')
  }

  const orderResponse = await api.post(
    '/billing/orders',
    {
      planCode: required(planCode, '\uAD6C\uB3C5 \uD50C\uB79C\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
      period,
      paymentMethod,
      couponCode,
      provider: PORTONE_V2_PROVIDER,
      channelCode: channelCode || config.defaultChannelCode,
      paymentMode: 'ONE_TIME',
    },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
  )
  const order = responseData(orderResponse)
  const orderNo = required(order?.orderNo, '\uACB0\uC81C \uC8FC\uBB38\uBC88\uD638\uB97C \uD655\uC778\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  const amount = Number(order?.amount)
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('\uACB0\uC81C \uAE08\uC561\uC744 \uD655\uC778\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  }

  savePendingPayment(order)
  const PortOne = await loadPortOneV2()
  const selectedChannel = config.channels.find(
    (channel) => channel?.code === order?.gatewayChannelCode,
  )
  config.channelKey = order?.channelKey
    || selectedChannel?.channelKey
    || config.channelKey
  const request = {
    storeId: required(config.storeId, '\uD3EC\uD2B8\uC6D0 \uC0C1\uC810 \uC124\uC815\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    channelKey: required(config.channelKey, '\uD3EC\uD2B8\uC6D0 \uCC44\uB110 \uC124\uC815\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.'),
    paymentId: orderNo,
    orderName: order?.planName ? `${order.planName} \uAD6C\uB3C5` : `${planCode} \uAD6C\uB3C5`,
    totalAmount: amount,
    currency: `CURRENCY_${String(order?.currency || 'KRW').toUpperCase()}`,
    payMethod: paymentMethod,
    redirectUrl,
    customData: JSON.stringify({ orderNo, orderType: 'SUBSCRIPTION' }),
  }
  if (customer && typeof customer === 'object') {
    request.customer = customer
  }

  const payment = await PortOne.requestPayment(request)
  if (!payment) {
    return { redirected: true, order }
  }
  if (payment.code) {
    clearPendingPayment()
    throw new Error(payment.message || '\uACB0\uC81C\uB97C \uC644\uB8CC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  }

  const paymentId = required(payment.paymentId || orderNo, '\uD3EC\uD2B8\uC6D0 \uACB0\uC81C\uBC88\uD638\uB97C \uD655\uC778\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  if (paymentId !== orderNo) {
    clearPendingPayment()
    throw new Error('\uACB0\uC81C\uBC88\uD638\uAC00 \uC8FC\uBB38\uC815\uBCF4\uC640 \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.')
  }

  const confirmed = responseData(await api.post('/billing/payments/confirm', {
    orderId: order.id,
    orderNo,
    provider: PORTONE_V2_PROVIDER,
    paymentKey: paymentId,
  }))
  clearPendingPayment()
  return { redirected: false, order, payment: confirmed }
}

export async function confirmPortOneV2RedirectPayment(search) {
  const params = searchParams(search)
  const errorCode = params.get('code')
  if (errorCode) {
    clearPendingPayment()
    throw new Error(params.get('message') || '\uACB0\uC81C\uB97C \uC644\uB8CC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.')
  }

  const paymentId = required(params.get('paymentId'), '\uD3EC\uD2B8\uC6D0 \uACB0\uC81C\uBC88\uD638\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.')
  const pending = readPendingPayment()
  if (pending?.orderNo && pending.orderNo !== paymentId) {
    throw new Error('\uACB0\uC81C\uBC88\uD638\uAC00 \uB300\uAE30 \uC911\uC778 \uC8FC\uBB38\uACFC \uC77C\uCE58\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4.')
  }

  const request = {
    orderNo: paymentId,
    provider: PORTONE_V2_PROVIDER,
    paymentKey: paymentId,
  }
  if (pending?.orderId != null) {
    request.orderId = pending.orderId
  }
  const payment = responseData(await api.post('/billing/payments/confirm', request))
  clearPendingPayment()
  return payment
}
